import React, { useEffect, useState } from "react";

import { Accordion, AccordionToggleEventHandler, Button, Divider, Spinner, Switch } from "@fluentui/react-components";

import PersonalAccessToken from "./components/personalAccessToken";
import Securities from "./components/securities";
import AvailableQuoteFeeds from "./components/availableQuoteFeeds";
import StopConnectionButton from "./components/StopConnectionButton";

import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import {
  selectSecuritiesValuesInStaging,
  selectSendingValues,
  setSecuritiesValuesState,
  setStagingState,
} from "../../service/store";
import { sendSecurityPrices } from "../../service/store/asyncThunks";

import { AccordionValue } from "./config";

import { useFeedManagerStyles } from "./feedManager.styles";
import { selectAutoUpdateEnabled, setAutoUpdate } from "./store";
import { selectIsConnected } from "../../taskpane/components/statusBar/store";
import { getDataOfSecuritiesFromDataProducerTable } from "../../service/producerServices";

import * as Sentry from "@sentry/react";
import { PricesResendManager } from "../../classes/pricesResendManager";
import { SentryCustomData } from "../../config";
import PostHogService from "../../service/PostHogService";

const FeedManager = () => {
  const [openItems, setOpenItems] = useState([AccordionValue.Token]);

  const styles = useFeedManagerStyles();

  const dispatch = useAppDispatch();

  const securitiesInStaging = useSelector(selectSecuritiesValuesInStaging);
  const isSendingValues = useSelector(selectSendingValues);
  const isAutoUpdateEnabled = useSelector(selectAutoUpdateEnabled);
  const isConnectionEstablished = useSelector(selectIsConnected);

  const securitiesInStagingIds = securitiesInStaging !== null ? Object.keys(securitiesInStaging) : [];

  const handleToggle: AccordionToggleEventHandler<string> = (_event, data) =>
    setOpenItems(data.openItems as AccordionValue[]);

  const sendPricesInStaging = async () => {
    try {
      for (const securityId of securitiesInStagingIds) {
        const updatedRow = securitiesInStaging[securityId].row;
        await dispatch(sendSecurityPrices({ securityId, updatedRow }));
      }

      PostHogService.capture('prices_sent_from_staging', {
        total_prices_sent: securitiesInStagingIds.length,
        component: 'feed_manager',
      });
    } catch (error) {
      console.error(error);

      Sentry.captureException({
        message: "Error sending prices in staging from the Update Pending button",
        error,
        level: "error",
      });
    }
  };

  const forceUpdateAllPrices = async () => {
    dispatch(setSecuritiesValuesState({}));
    dispatch(setStagingState({}));

    try {
      PricesResendManager.getInstance().resetManager();

      const securitiesToBeSent = await getDataOfSecuritiesFromDataProducerTable();
      for (const security of securitiesToBeSent) {
        const securityId = security.securityProducerId;
        const updatedRow = security.row;

        await dispatch(sendSecurityPrices({ securityId, updatedRow, data: security }));
      }

      PostHogService.capture('force_update_all_prices', {
        total_prices_sent: securitiesToBeSent.length,
        component: 'feed_manager',
      });
    } catch (error) {
      console.error(error);

      Sentry.captureException({
        message: "Error sending prices in DataProducer table from the Force Update All button",
        error,
        level: "error",
      });
    }
  };

  useEffect(() => {
    if (!isAutoUpdateEnabled || !isConnectionEstablished) {
      return;
    }

    sendPricesInStaging();
  }, [isAutoUpdateEnabled, isConnectionEstablished]);

  return (
    <div className={styles.root}>
      <Accordion multiple openItems={openItems} onToggle={handleToggle} collapsible>
        <PersonalAccessToken />
        <Divider />
        <Securities />
        <Divider />
        <AvailableQuoteFeeds />
      </Accordion>

      <div className={styles.dialogButtons}>
        <StopConnectionButton />

        <Switch
          label="Auto-Update"
          checked={isAutoUpdateEnabled}
          onChange={() => {
            dispatch(setAutoUpdate(!isAutoUpdateEnabled));

            Sentry.setExtra(SentryCustomData.AUTO_UPDATE_ENABLED, !isAutoUpdateEnabled);

            PostHogService.capture('auto_update_toggled', {
              status: !isAutoUpdateEnabled ? 'enabled' : 'disabled',
            });
          }}
          labelPosition="before"
          disabled={!isConnectionEstablished}
        />

        <Button
          disabled={
            isSendingValues || !securitiesInStagingIds.length || isAutoUpdateEnabled || !isConnectionEstablished
          }
          icon={isSendingValues ? <Spinner size="extra-tiny" /> : null}
          onClick={sendPricesInStaging}
          appearance="primary"
        >
          Update Pending
        </Button>

        <Button
          disabled={!isConnectionEstablished}
          icon={isSendingValues ? <Spinner size="extra-tiny" /> : null}
          onClick={forceUpdateAllPrices}
          appearance="primary"
        >
          Force Update All
        </Button>
      </div>
    </div>
  );
};

export default FeedManager;
